import { GroupVisibility, GroupWhere } from '@bp/bp-graphql-types';
import type { TimetableViewEntryType } from '@bp/ui-components';
import { graphql } from 'client/gql';
import type { TimetableDay } from 'components/Timetable/Timetable';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import dayjs, { type Dayjs } from 'dayjs';
import { useContext } from 'react';
import { useQuery } from 'urql';
import { CONNECT_DATE_TEMPLATE } from '../utils/connectLib';
import { useAuthClaims } from './useAuthClaims';

const TimetableEventsQueryDocument = graphql(`
  query TimetableEventsQueryDocumentData(
    $calendarEventWhere: CalendarEventWhere!
    $schoolYearUuid: ID!
    $organizationUuid: ID!
  ) {
    calendarEvents(where: $calendarEventWhere) {
      __typename
      uuid
      title
      start
      duration
      classShortNames
      classbookHomework
      classbookHomeworkContentType
      holder {
        __typename
        ... on Group {
          __typename
          uuid
          grades
          subject {
            __typename
            uuid
            name
            shortName
            color
          }
          classOrClassGroup {
            __typename
            ... on Class {
              __typename
              uuid
              color
              name
              shortName
              grade
              gradeGroup
            }
            ... on ClassGroup {
              __typename
              uuid
              name
              shortName
              classColor
              division {
                __typename
                uuid
                class {
                  __typename
                  uuid
                }
              }
            }
          }
        }
      }
    }
    classes(where: { schoolYear: { uuid: $schoolYearUuid }, organization: { uuid: $organizationUuid } }) {
      __typename
      uuid
      name
      shortName
      divisions {
        __typename
        uuid
      }
    }
  }
`);

export const useTimetable = (
  profileUuid: string,
  preloadDates?: Date[],
  context?: 'classbook' | 'dashboard' | 'homework',
) => {
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const { currentSchoolYear } = useContext(OrganizationConfigContext);

  const groupWhere: GroupWhere = {
    OR: [
      { showLessonEventsInClassbook: context === 'classbook' },
      { showImportedLessonEvents: context === 'dashboard' || context === 'homework' },
    ],
  };

  if (context === 'dashboard' || context === 'homework') {
    groupWhere.visibility = GroupVisibility.Full;
  }

  const [{ data }] = useQuery({
    query: TimetableEventsQueryDocument,
    variables: {
      calendarEventWhere: {
        categories_INCLUDES: 'lesson',
        holder: {
          Group: groupWhere,
        },
        OR: [{ attendees_SOME: { uuid: profileUuid } }, { owner: { uuid: profileUuid } }],
        ...(preloadDates
          ? {
              daysConnection_SOME: {
                OR: preloadDates.map((d) => ({
                  node: { date_STARTS_WITH: dayjs(d).format(CONNECT_DATE_TEMPLATE) },
                })),
              },
            }
          : {}),
      },
      schoolYearUuid: currentSchoolYear.uuid,
      organizationUuid,
    },
  });

  const events = data?.calendarEvents ?? [];
  const classes = data?.classes ?? [];

  /**
   * Returns start and end times for TimetableView
   *
   * @returns object with start and end in Dayjs format
   */
  function getTimetableStartEnd(): { start: Dayjs; end: Dayjs } {
    // TODO: hardcoded for now -> get start and end from timegrid or school config?
    const timetableStart = dayjs().set('hour', 6);
    const timetableEnd = dayjs().set('hour', 18);
    return { start: timetableStart, end: timetableEnd };
  }

  /**
   * Generates an array of timetable days starting from a given date.
   * If more than one days are requested, the timetable will start from the past Monday.
   *
   * @param date - The reference date to generate timetable days from
   * @param days - The number of days to generate (1, 5, 6, 7). Defaults to 5
   * @param selectedUuid
   * @param colorSource
   * @param onEventClick
   * @returns Array of TimetableDay objects containing date, day of week name and array of TimetableEntries for this day
   *
   * @example
   * // Generate 5 days starting from Monday for a given date
   * const timetableDays = getTimetableDays(dayjs('2023-01-01'), 5);
   */
  function getTimetableDays(
    date: Dayjs,
    days: 1 | 2 | 3 | 4 | 5 | 6 | 7 = 5,
    selectedUuid?: string,
    onEventClick?: ({
      eventUuid,
      eventTitle,
      subjectUuid,
      groupUuid,
    }: {
      eventUuid: string;
      eventTitle?: string | null;
      subjectUuid?: string | null;
      groupUuid?: string | null;
    }) => void,
    colorSource: 'class' | 'subject' = 'class',
  ): TimetableDay[] {
    const timetableDays: TimetableDay[] = [];

    const singleDay = days === 1;

    const start = singleDay ? date : date.day() === 0 ? date.subtract(1, 'day').day(1) : date.day(1);

    for (let i = 0; i < days; i++) {
      const date = start.add(i, 'days');

      const sameDayEvents = events.filter(({ start }) => dayjs(start).isSame(date, 'day')) ?? [];

      const entries: TimetableViewEntryType[] = sameDayEvents.map(
        ({ uuid, start, duration, holder, title, classShortNames }) => {
          const durationInMinutes = isNaN(Number(duration)) ? 0 : Number(duration) / 60;

          let subject: string | null = null;
          let subjectShortName: string | null = null;
          const shortNames: string[] = [];
          let color: string | null = null;

          const shortNameMap = new Map<string, { parentShort: string; groups: Set<string> }>();

          if (holder.__typename === 'Group') {
            subject = holder.subject?.shortName ?? null;
            subjectShortName = holder.subject?.name ?? null;

            if (!color && colorSource === 'subject') {
              color = holder.subject?.color ?? null;
            }

            holder.classOrClassGroup.forEach((classOrGroup) => {
              if (classOrGroup.__typename === 'Class') {
                const key = classOrGroup.uuid;
                if (!shortNameMap.has(key)) {
                  shortNameMap.set(key, { parentShort: classOrGroup.shortName, groups: new Set() });
                }
                if (!color && colorSource === 'class') {
                  color = classOrGroup.color ?? null;
                }
              }

              if (classOrGroup.__typename === 'ClassGroup') {
                const parentClass = classes.find((c) => c.divisions.some((d) => d.uuid === classOrGroup.division.uuid));
                if (parentClass) {
                  const key = parentClass.uuid;
                  if (!shortNameMap.has(key)) {
                    shortNameMap.set(key, { parentShort: parentClass.shortName, groups: new Set() });
                  }
                  shortNameMap.get(key)!.groups.add(classOrGroup.shortName);
                } else {
                  const key = `group-${classOrGroup.uuid}`;
                  if (!shortNameMap.has(key)) {
                    shortNameMap.set(key, { parentShort: classOrGroup.shortName, groups: new Set() });
                  }
                }
                if (!color && colorSource === 'class') {
                  color = classOrGroup.classColor ?? null;
                }
              }
            });

            // sort !
            const parentEntries = Array.from(shortNameMap.values()).sort((a, b) =>
              a.parentShort.localeCompare(b.parentShort),
            );
            for (const { parentShort, groups } of parentEntries) {
              if (groups.size > 0) {
                const sortedGroups = Array.from(groups).sort();
                shortNames.push(`${parentShort} (${sortedGroups.join(', ')})`);
              } else {
                shortNames.push(parentShort);
              }
            }
          }

          return {
            uuid: uuid,
            start: dayjs(start).toDate(),
            end: dayjs(start).add(durationInMinutes, 'minutes').toDate(),
            field1: subject ?? '',
            field4: shortNames?.join(', '),
            tooltip: {
              field1: subjectShortName ?? '',
              field4: classShortNames?.join(', ') ?? '',
            },
            bgColor: color ?? undefined,
            selected: selectedUuid ? selectedUuid === uuid : undefined,
            onClick: onEventClick
              ? () =>
                  onEventClick({
                    eventUuid: uuid,
                    eventTitle: title,
                    subjectUuid: holder.__typename === 'Group' ? holder.subject?.uuid : null,
                    groupUuid: holder.__typename === 'Group' ? holder.uuid : null,
                  })
              : undefined,
          };
        },
      );

      timetableDays.push({
        date,
        dayOfWeek: date.format('dddd'),
        entries,
      });
    }

    return timetableDays;
  }

  return { getTimetableStartEnd, getTimetableDays, events };
};
